import {
  Badge,
  Group,
  InputWrapper,
  MultiSelect,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import { useForm, UseFormReturnType } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import _ from "lodash"
import { zodResolver } from "mantine-form-zod-resolver"
import { useEffect, useMemo, useState } from "react"
import { flushSync } from "react-dom"
import { useNavigate, useParams } from "react-router-dom"
import { z } from "zod"

import {
  BudgetsRequests,
  BudgetsResponses,
} from "@costory/types/endpoints/budgets"
import { VirtualDimensionsResponses } from "@costory/types/endpoints/virtualDimensions"
import { BudgetStatus, CostType, Currency } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"

import { useUnsavedChangesBlocker } from "@costory/front/components/common/useUnsavedChangesBlocker"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { BudgetSaveEditToolbar } from "@costory/front/pages/Budgets/components/BudgetSaveEdittoolbar"
import { BudgetTableHead } from "@costory/front/pages/Budgets/components/BudgetTableHead"
import { BudgetToolbar } from "@costory/front/pages/Budgets/components/BudgetToolBar"
import { EditableBudgetTableRow } from "@costory/front/pages/Budgets/components/EditableBudgetTableRow"
import { EmptyBudgetTable } from "@costory/front/pages/Budgets/components/EmptyBudgetTable"
import { BudgetTableMode } from "@costory/front/pages/Budgets/consts"
import {
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
} from "@costory/front/queries/budgets"
import { useVirtualDimensionCostQuery } from "@costory/front/queries/virtualDimensions"

interface ToolbarFormValues
  extends Omit<BudgetsRequests.CreateBudget, "year" | "month" | "lines"> {
  startDate: string
}

export type TableRowFormValues = {
  allocations: number[]
  ownerId: string | null
}
type TableFormValues = Record<string, TableRowFormValues>

export type BudgetFormValues = ToolbarFormValues & {
  lines: TableFormValues
}

export const BudgetTable = ({
  formData,
  versionsAvailableToCompare,
  lastVersion,
  mode,
}: {
  formData: BudgetsResponses.BudgetFormData
  versionsAvailableToCompare: BudgetsResponses.BudgetDetail[]
  lastVersion?: BudgetsResponses.BudgetDetail
  mode: BudgetTableMode
}) => {
  const [selectedVersionsIds, setSelectedVersionsIds] = useState<string[]>([])
  const selectedVersions = useMemo(() => {
    return versionsAvailableToCompare.filter((version) =>
      selectedVersionsIds?.includes(version.versionId),
    )
  }, [selectedVersionsIds, versionsAvailableToCompare])
  const { budgetId } = useParams<{ budgetId?: string }>()
  const navigate = useNavigate()
  const {
    mutateAsync: createBudgetVersion,
    isPending: isCreateBudgetMutationPending,
  } = useCreateBudgetMutation()
  const {
    mutateAsync: updateBudget,
    isPending: isUpdateBudgetMutationPending,
  } = useUpdateBudgetMutation()

  const isFormPending =
    isCreateBudgetMutationPending || isUpdateBudgetMutationPending
  const availableVersionSelectItems = useMemo(() => {
    return (
      versionsAvailableToCompare.map((version) => ({
        value: version.versionId,
        label:
          version.status == "DRAFT"
            ? "Current Draft"
            : version.versionName && version.versionName.length > 0
              ? version.versionName
              : `${dayjs(version.createdAt).format("DD MMM YYYY hh:mm:ss")}`,
      })) ?? []
    )
  }, [versionsAvailableToCompare])

  const form = useForm<
    BudgetFormValues,
    (values: BudgetFormValues) => BudgetFormValues
  >({
    initialValues: {
      versionName: lastVersion?.versionName ?? "",
      costType: lastVersion?.costType ?? CostType.cost,
      parentBudgetId: budgetId,
      name: lastVersion?.name ?? "",
      tagsIds: lastVersion?.tags.map((t) => t.id) ?? [],
      virtualDimensionId: lastVersion?.virtualDimensionId ?? "",
      startDate: lastVersion
        ? dayjs()
            .year(lastVersion.year)
            .month(lastVersion.startMonth)
            .add(1, "day")
            .startOf("month")
            .format(DEFAULT_DATE_FORMAT)
        : dayjs().startOf("year").format(DEFAULT_DATE_FORMAT),
      excludedValues: lastVersion?.excludedValues ?? [],
      currency: lastVersion?.currency ?? Currency.USD,
      status: lastVersion?.status ?? BudgetStatus.DRAFT,
      monitorBudget: lastVersion?.monitorBudget ?? false,
      lines:
        lastVersion?.lines.reduce((acc, line) => {
          return {
            ...acc,
            [line.virtualDimensionValue]: line,
          }
        }, {}) ?? {},
    },
    validate: (values) =>
      zodResolver(
        z.object({
          name: z.string().min(3, "Name must be at least 3 characters"),
          startDate: z.string(),
          virtualDimensionId: z.string(),
          tagsIds: z.array(z.string()),
        }),
      )(values as unknown as Record<string, unknown>),
  })

  const monthsOffset = dayjs(form.getValues().startDate).month()

  useUnsavedChangesBlocker({
    isDirtyCallback: () => form.isDirty(),
    onConfirmCallback: () => form.resetDirty(),
  })

  const {
    virtualDimensionId,
    startDate,
    excludedValues,
    costType,
    currency,
    versionName,
  } = form.getValues()
  const currentRevisionName =
    versionName && versionName.length > 0
      ? versionName + " (Current)"
      : "Current budget"
  const selectedVirtualDimension = formData.availableVirtualDimensions.find(
    (vd) => vd.id === virtualDimensionId,
  )
  const query = useVirtualDimensionCostQuery({
    id: virtualDimensionId,
    startDate,
    costType,
    currency,
  })

  const onLineChange = (
    values: TableRowFormValues,
    virtualDimensionValue: string,
  ) => {
    form.setFieldValue(`lines.${virtualDimensionValue}`, values)
  }

  // Reset lines when the virtual dimension changes
  useEffect(() => {
    // Prevents resetting lines when the form is initialized
    if (mode !== "create") {
      const currentLines = form.getValues().lines
      form.setFieldValue(
        "lines",
        selectedVirtualDimension?.values.reduce((acc, value) => {
          return {
            ...acc,
            [value]: currentLines[value] || {
              allocations: Array(12).fill(0),
              ownerId: "",
              virtualDimensionValue: value,
            },
          }
        }, {}) ?? {},
      )

      return
    }

    form.setFieldValue(
      "lines",
      selectedVirtualDimension?.values.reduce((acc, value) => {
        return {
          ...acc,
          [value]: {
            allocations: Array(12).fill(0),
            ownerId: "",
            virtualDimensionValue: value,
          },
        }
      }, {}) ?? {},
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVirtualDimension])

  return (
    <Stack>
      {mode === BudgetTableMode.View && lastVersion ? (
        <Stack>
          <Group>
            <Group align="end">
              <Title order={2}>
                {lastVersion.name} - {dayjs(startDate).format("MMMM YYYY")} /{" "}
                {dayjs(startDate).add(11, "month").format("MMMM YYYY")}
              </Title>
              <Text>Owned by: {lastVersion.createdBy.firstName}</Text>
            </Group>
            {lastVersion.tags.map((tag) => (
              <Badge key={tag.name} color={tag.color}>
                {tag.name}
              </Badge>
            ))}
          </Group>
          <Group>
            <InputWrapper label="Compare to other versions">
              <MultiSelect
                data={availableVersionSelectItems}
                value={selectedVersionsIds}
                onChange={setSelectedVersionsIds}
                placeholder="Select versions"
              />
            </InputWrapper>
          </Group>
        </Stack>
      ) : (
        <BudgetToolbar
          form={form}
          availableVersionSelectItems={availableVersionSelectItems}
          selectedVersionsIds={selectedVersionsIds}
          setSelectedVersionsIds={setSelectedVersionsIds}
          initOptionsCanBeEdited={
            mode === BudgetTableMode.Create ||
            (versionsAvailableToCompare.length === 0 &&
              lastVersion?.status === BudgetStatus.DRAFT)
          }
          mode={mode}
          availableVirtualDimensions={formData.availableVirtualDimensions}
          availableTechnicalMetrics={formData.availableTechnicalMetrics}
        />
      )}
      {selectedVirtualDimension &&
      selectedVirtualDimension.values.filter(
        (el) => !excludedValues.includes(el),
      ).length > 0 ? (
        <QueryWrapper query={query}>
          {({ data: { actualCosts, forecastedCosts } }) => {
            const sortedLines = getComputedLines({
              vdimValues: selectedVirtualDimension.values,
              excludedValues,
              form,
              mode,
              lastVersion,
              selectedVersions,
              actualCosts,
              forecastedCosts,
            })
            return (
              <Paper
                flex={1}
                mah={mode === BudgetTableMode.View ? "68vh" : "65vh"}
                styles={{ root: { overflowY: "scroll" } }}
                p={0}
              >
                <table
                  style={{
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <BudgetTableHead monthsOffset={monthsOffset} />
                  <tbody>
                    {sortedLines.map((line) => (
                      <EditableBudgetTableRow
                        key={line.virtualDimensionValue}
                        currentRevisionName={currentRevisionName}
                        revisions={line.revisions}
                        actualCosts={line.actualCosts}
                        forecastedCosts={line.forecastedCosts}
                        virtualDimensionValue={line.virtualDimensionValue}
                        values={line}
                        onChange={(values) => {
                          onLineChange(values, line.virtualDimensionValue)
                        }}
                        virtualDimensionId={virtualDimensionId}
                        startDate={startDate}
                        mode={mode}
                        formData={formData}
                        currency={currency}
                        costType={costType}
                      />
                    ))}
                  </tbody>
                </table>
              </Paper>
            )
          }}
        </QueryWrapper>
      ) : (
        <EmptyBudgetTable />
      )}
      <BudgetSaveEditToolbar
        mode={mode}
        isFormPending={isFormPending}
        lastVersion={lastVersion}
        handleClickSave={(status) =>
          handleClickSave({
            status,
            form,
            updateBudget,
            navigate,
          })
        }
        handleClickCreate={(status) =>
          handleClickCreate({
            status,
            form,
            createBudgetVersion,
            navigate,
          })
        }
        handleClickCreateRevision={(status) =>
          handleClickCreateRevision({
            status,
            form,
            createBudgetVersion,
            navigate,
          })
        }
      />
    </Stack>
  )
}

const handleClickCreateRevision = async ({
  status,
  form,
  createBudgetVersion,
  navigate,
}: {
  status: BudgetStatus
  form: UseFormReturnType<BudgetFormValues>
  createBudgetVersion: (
    values: BudgetsRequests.CreateBudget,
  ) => Promise<BudgetsResponses.BudgetDetail>
  navigate: (path: string) => void
}) => {
  const res = form.validate()
  if (res.hasErrors) {
    return
  }
  const { startDate, lines, ...formValues } = form.getValues()
  const formatedLines = Object.keys(lines).map((key) => ({
    ...lines[key],
    virtualDimensionValue: key,
  }))

  const submittedValues = {
    ...formValues,
    status,
    year: dayjs(startDate).year(),
    month: dayjs(startDate).month(),
    lines: formatedLines,
  }
  try {
    const createdBudget = await createBudgetVersion(submittedValues)
    showNotification({
      title: "Revision created",
      message: "Your revision has been created",
      color: "green",
      position: "top-right",
    })
    flushSync(() => {
      form.resetDirty()
    })
    navigate(`/budgets/${createdBudget.parentBudgetId}`)
  } catch (error) {
    showNotification({
      title: "An error occured",
      message: error as string,
      color: "red",
      position: "top-right",
    })
  }
}

const handleClickSave = async ({
  status,
  form,
  updateBudget,
  navigate,
}: {
  status: BudgetStatus
  form: UseFormReturnType<BudgetFormValues>
  updateBudget: (
    values: BudgetsRequests.UpdateBudget & { id: string },
  ) => Promise<BudgetsResponses.BudgetDetail>
  navigate: (path: string) => void
}) => {
  const res = form.validate()
  if (res.hasErrors) {
    return
  }
  const { startDate, lines, ...formValues } = form.getValues()

  const formatedLines = Object.keys(lines).map((key) => ({
    ...lines[key],
    virtualDimensionValue: key,
  }))

  const submittedValues = {
    ...formValues,
    id: form.getValues().parentBudgetId!,
    year: dayjs(startDate).year(),
    month: dayjs(startDate).month(),
    lines: formatedLines,
    status,
  }

  try {
    await updateBudget(submittedValues)
    showNotification({
      title: "Budget updated",
      message: "Your budget has been updated",
      color: "green",
      position: "top-right",
    })
    flushSync(() => {
      form.resetDirty()
    })
    navigate("/budgets")
  } catch (error) {
    showNotification({
      title: "An error occured",
      message: error as string,
      color: "red",
      position: "top-right",
    })
  }
}

const handleClickCreate = async ({
  status,
  form,
  createBudgetVersion,
  navigate,
}: {
  status: BudgetStatus
  form: UseFormReturnType<BudgetFormValues>
  createBudgetVersion: (
    values: BudgetsRequests.CreateBudget,
  ) => Promise<BudgetsResponses.BudgetDetail>
  navigate: (path: string) => void
}) => {
  const res = form.validate()
  if (res.hasErrors) {
    return
  }
  const { startDate, lines, ...formValues } = form.getValues()
  const formatedLines = Object.keys(lines).map((key) => ({
    ...lines[key],
    virtualDimensionValue: key,
  }))

  const submittedValues = {
    ...formValues,
    status,
    year: dayjs(startDate).year(),
    month: dayjs(startDate).month(),
    lines: formatedLines,
  }
  try {
    const createdBudget = await createBudgetVersion(submittedValues)
    flushSync(() => {
      form.resetDirty()
    })
    showNotification({
      title: "Budget created",
      message: "Your budget has been created",
      color: "green",
      position: "top-right",
    })
    navigate(`/budgets/${createdBudget.parentBudgetId}`)
  } catch (error) {
    showNotification({
      title: "An error occured",
      message: error as string,
      color: "red",
      position: "top-right",
    })
  }
}

function getTotalLine(
  allBudgetLines: {
    allocations: number[]
    revisions: {
      name: string
      values: number[]
      versionStatus: BudgetStatus
      lastEdited: Date
    }[]
    virtualDimensionValue: string
    ownerId: string | null
    actualCosts: (number | null)[]
    forecastedCosts: (number | null)[]
  }[],
) {
  const allocations = allBudgetLines.reduce(
    (acc, line) => acc.map((value, index) => value + line.allocations[index]),
    new Array(12).fill(0),
  )
  const actualCosts = allBudgetLines.reduce(
    (acc, line) => acc.map((value, index) => value + line.actualCosts[index]),
    new Array(12).fill(0),
  )
  const forecastedCosts = allBudgetLines.reduce(
    (acc, line) =>
      acc.map(
        (value, index) =>
          value + (line.forecastedCosts[index] ?? line.actualCosts[index] ?? 0),
      ),
    new Array(12).fill(0),
  )
  const revisions = _(
    allBudgetLines
      .map((el) =>
        el.revisions.map((ee) => ({
          ...ee,
          virtualDimensionValue: el.virtualDimensionValue,
        })),
      )
      .flat(),
  )
    .groupBy((e) => e.name)
    .map((e) => ({
      ...e[0],
      values: e.reduce((acc, ee) => {
        return acc.map((value, index) => value + ee.values[index])
      }, new Array(12).fill(0)),
    }))
    .value()
  return {
    allocations,
    revisions,
    virtualDimensionValue: "Total",
    ownerId: null,
    actualCosts,
    forecastedCosts,
  }
}

const getComputedLines = ({
  vdimValues,
  excludedValues,
  form,
  mode,
  lastVersion,
  selectedVersions,
  actualCosts,
  forecastedCosts,
}: {
  vdimValues: string[]
  excludedValues: string[]
  form: UseFormReturnType<BudgetFormValues>
  mode: BudgetTableMode
  lastVersion: BudgetsResponses.BudgetDetail | undefined
  selectedVersions: BudgetsResponses.BudgetDetail[]
  actualCosts: VirtualDimensionsResponses.GetVirtualDimensionCost["actualCosts"]
  forecastedCosts: VirtualDimensionsResponses.GetVirtualDimensionCost["forecastedCosts"]
}) => {
  const lastVersionName =
    lastVersion?.versionName && lastVersion?.versionName.length > 0
      ? lastVersion?.versionName
      : `Current budget ${dayjs(lastVersion?.createdAt || new Date()).format("DD MMM YYYY hh:mm:ss")}`
  const allBudgetLines = vdimValues
    .filter((el) => !excludedValues.includes(el))
    .map((virtualDimensionValue) => {
      const budgetLine = form.getValues().lines[virtualDimensionValue]

      if (!budgetLine) {
        // Defensive: If missing, return a default line to avoid runtime errors
        return {
          allocations: new Array(12).fill(0),
          revisions: [],
          virtualDimensionValue,
          ownerId: null,
          actualCosts:
            actualCosts[virtualDimensionValue] ?? new Array(12).fill(null),
          forecastedCosts:
            forecastedCosts[virtualDimensionValue] ?? new Array(12).fill(null),
        }
      }

      const isCreateMode = mode === "create"

      const lastVersionLine = !isCreateMode
        ? lastVersion!.lines.find(
            (line) => line.virtualDimensionValue === virtualDimensionValue,
          )
        : undefined
      const revisions = lastVersionLine
        ? [
            {
              name: lastVersionName,
              values: lastVersionLine.allocations,
              versionStatus: lastVersion?.status ?? BudgetStatus.DRAFT,
              lastEdited: lastVersion?.createdAt ?? new Date(),
            },
            ...selectedVersions.map((version) => {
              const line = version.lines.find(
                (line) => line.virtualDimensionValue === virtualDimensionValue,
              )
              return {
                name:
                  version.versionName && version.versionName.length > 0
                    ? version.versionName
                    : `Revision ${dayjs(version!.createdAt).format("DD MMM YYYY hh:mm:ss")}`,
                values: line?.allocations ?? new Array(12).fill(null),
                versionStatus: version.status,
                lastEdited: version.createdAt,
              }
            }),
          ]
        : [
            {
              name: lastVersionName,
              values: budgetLine?.allocations ?? new Array(12).fill(null),
              versionStatus: lastVersion?.status ?? BudgetStatus.DRAFT,
              lastEdited: lastVersion?.createdAt ?? new Date(),
            },
          ]

      return {
        allocations: budgetLine.allocations,
        revisions,
        virtualDimensionValue,
        ownerId: budgetLine.ownerId,
        actualCosts: actualCosts[virtualDimensionValue],
        forecastedCosts: forecastedCosts[virtualDimensionValue],
      }
    })
    .filter((line) => line !== null)
  const valuesLines = allBudgetLines.filter(
    (line) => line.virtualDimensionValue !== "Others",
  )
  const othersLine = allBudgetLines.find(
    (line) => line.virtualDimensionValue === "Others",
  )

  const totalLine = getTotalLine(allBudgetLines)
  return [totalLine, ...valuesLines, ...(othersLine ? [othersLine] : [])]
}
